import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faPlus, 
  faSearch, 
  faSpinner,
  faFileExcel,
  faFileCsv,
  faEye,
  faUser,
  faFileAlt,
  faCalendarAlt,
  faChevronRight,
  faChevronLeft,
  faClipboardList,
  faDownload,
  faCheckSquare,
  faCheckCircle,
  faClock,
  faVolumeDown,
  faTimesCircle,
  faBan,
  faCalendarCheck,
  faPhoneSlash,
  faPhoneAlt,
  faChartBar,
  faChevronDown,
  faChevronUp,
  faInfoCircle
} from '@fortawesome/free-solid-svg-icons';
import api from '../../Api';

const InterviewUploads = () => {
  const navigate = useNavigate();
  const { surveyId } = useParams();
  const { user } = useAuth();
  const [view, setView] = useState('surveys'); // 'surveys' or 'uploads'
  const [selectedSurvey, setSelectedSurvey] = useState(null);
  const [surveys, setSurveys] = useState([]);
  const [uploads, setUploads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingSurveys, setLoadingSurveys] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchInputValue, setSearchInputValue] = useState(''); // Local input value that doesn't trigger API calls
  const [pagination, setPagination] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedUploads, setSelectedUploads] = useState([]);
  const [exporting, setExporting] = useState(false);
  const [surveyStats, setSurveyStats] = useState(null);
  const [loadingStats, setLoadingStats] = useState(false);
  const [showFilesSection, setShowFilesSection] = useState(false);
  const [lines, setLines] = useState([]);
  const [linesPagination, setLinesPagination] = useState({});
  const [loadingLines, setLoadingLines] = useState(false);
  const [linesStatusFilter, setLinesStatusFilter] = useState('all');
  const [linesCurrentPage, setLinesCurrentPage] = useState(1);
  const [linesSearchTerm, setLinesSearchTerm] = useState('');
  const [exportingLines, setExportingLines] = useState(false);
  const linesSectionRef = useRef(null);
  const [surveySearchTerm, setSurveySearchTerm] = useState('');
  const [surveyFilter, setSurveyFilter] = useState('all'); // 'all', 'completed', 'pending'
  const [searchResults, setSearchResults] = useState([]); // Lines from global search
  const [searchResultsPagination, setSearchResultsPagination] = useState({});
  const [searchResultsPage, setSearchResultsPage] = useState(1);
  const [isSearchMode, setIsSearchMode] = useState(false);
  const [isCardFlipped, setIsCardFlipped] = useState(false);
  const [surveysPage, setSurveysPage] = useState(1);
  const SURVEYS_PER_PAGE = 20;

  // Load survey from URL params on mount or when surveyId changes
  useEffect(() => {
    if (surveyId) {
      // Set view to uploads immediately to prevent flash of surveys list
      setView('uploads');
      // Fetch the survey by ID and set it as selected
      const loadSurveyFromUrl = async () => {
        try {
          setLoadingSurveys(true);
          const response = await api.get('/cati/interview-uploads/surveys');
          const surveysList = response.data.success ? (response.data.data || []) : (response.data || []);
          const survey = surveysList.find(s => s.id.toString() === surveyId.toString());
          if (survey) {
            setSelectedSurvey(survey);
            setCurrentPage(1);
            setSearchTerm('');
            setSearchInputValue(''); // Reset local search input value
            setLinesCurrentPage(1);
            setLinesStatusFilter('all');
            setLinesSearchTerm('');
            setLines([]);
            setIsCardFlipped(false); // Reset flip state when survey changes
          } else {
            // Survey not found, redirect to surveys list
            navigate('/cati/interview-uploads');
          }
        } catch (error) {
          console.error('Error loading survey:', error);
          navigate('/cati/interview-uploads');
        } finally {
          setLoadingSurveys(false);
        }
      };
      loadSurveyFromUrl();
    } else {
      // No surveyId in URL, show surveys list
      if (view !== 'surveys') {
        setView('surveys');
      }
      if (selectedSurvey !== null) {
        setSelectedSurvey(null);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [surveyId]);

  useEffect(() => {
    if (view === 'surveys') {
      // Only fetch surveys if not in search mode
      if (!isSearchMode || (!surveySearchTerm && surveyFilter === 'all')) {
        fetchSurveys();
        setIsSearchMode(false);
      }
    } else if (view === 'uploads' && selectedSurvey) {
      fetchUploads();
      fetchSurveyStats();
      fetchLines();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, selectedSurvey, currentPage, searchTerm]); // searchTerm here is the actual search that triggers API

  useEffect(() => {
    if (view === 'uploads' && selectedSurvey) {
      // Fetch lines when status filter or page changes (but not on search term change)
      fetchLines();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [linesStatusFilter, linesCurrentPage, selectedSurvey]);

  useEffect(() => {
    if (view === 'uploads' && selectedSurvey) {
      fetchSurveyStats();
    } else {
      setSurveyStats(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, selectedSurvey]);

  const fetchSurveys = async () => {
    try {
      setLoadingSurveys(true);
      const response = await api.get('/cati/interview-uploads/surveys');
      
      if (response.data.success) {
        setSurveys(response.data.data || []);
        setSurveysPage(1);
      }
    } catch (error) {
      console.error('Error fetching surveys:', error);
    } finally {
      setLoadingSurveys(false);
    }
  };

  const performGlobalSearch = async (page = 1, filterOverride = null) => {
    try {
      setLoadingSurveys(true);
      setIsSearchMode(true);
      
      const params = new URLSearchParams({
        page: page,
        per_page: 20
      });
      
      // Use filterOverride if provided, otherwise use current surveyFilter state
      const activeFilter = filterOverride !== null ? filterOverride : surveyFilter;
      
      // Add search parameter if provided
      if (surveySearchTerm && surveySearchTerm.trim() !== '') {
        params.append('search', surveySearchTerm.trim());
      }
      
      // Add completion filter
      if (activeFilter === 'completed') {
        params.append('completed', 'true');
      } else if (activeFilter === 'pending') {
        // En attente: pending lines from active uploads only (not_done)
        params.append('completed', 'false');
        params.append('upload_status', 'not_done');
      } else if (activeFilter === 'deactivated') {
        // Désactivé: pending lines from deactivated uploads (reported)
        params.append('completed', 'false');
        params.append('upload_status', 'reported');
      }
      
      const response = await api.get(`/cati/interviewer/lines?${params}`);
      
      if (response.data.success) {
        const data = response.data.data;
        setSearchResults(data.data || []);
        setSearchResultsPagination({
          current_page: data.current_page,
          last_page: data.last_page,
          per_page: data.per_page,
          total: data.total,
          from: data.from,
          to: data.to
        });
        setSearchResultsPage(page);
      }
    } catch (error) {
      console.error('Error performing global search:', error);
      setSearchResults([]);
    } finally {
      setLoadingSurveys(false);
    }
  };

  const fetchUploads = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: currentPage,
        per_page: 15,
        survey_id: selectedSurvey.id
      });
      
      if (searchTerm) {
        params.append('search', searchTerm);
      }

      const response = await api.get(`/cati/interview-uploads?${params}`);
      
      if (response.data.success) {
        const data = response.data.data;
        setUploads(data.data || []);
        setPagination({
          current_page: data.current_page,
          last_page: data.last_page,
          per_page: data.per_page,
          total: data.total,
          from: data.from,
          to: data.to
        });
      }
    } catch (error) {
      console.error('Error fetching uploads:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSurveyStats = async () => {
    if (!selectedSurvey) return;
    
    try {
      setLoadingStats(true);
      const response = await api.get(`/cati/interview-uploads/stats?survey_id=${selectedSurvey.id}`);
      
      if (response.data.success) {
        setSurveyStats(response.data.stats);
      }
    } catch (error) {
      console.error('Error fetching survey stats:', error);
    } finally {
      setLoadingStats(false);
    }
  };

  const fetchLines = async (searchTerm = null) => {
    if (!selectedSurvey) return;
    
    try {
      setLoadingLines(true);
      const params = new URLSearchParams({
        page: linesCurrentPage,
        per_page: 20,
        survey_id: selectedSurvey.id
      });
      
      if (linesStatusFilter !== 'all') {
        if (linesStatusFilter === 'pending') {
          // En attente: pending lines from active uploads only (not_done)
          params.append('completed', 'false');
          params.append('upload_status', 'not_done');
        } else if (linesStatusFilter === 'deactivated') {
          // Désactivé: pending lines from deactivated uploads (reported)
          params.append('completed', 'false');
          params.append('upload_status', 'reported');
        } else if (linesStatusFilter === 'completed') {
          params.append('completed', 'true');
        } else {
          params.append('result_status', linesStatusFilter);
        }
      }

      // Add search parameter if provided
      const search = searchTerm !== null ? searchTerm : linesSearchTerm;
      if (search && search.trim() !== '') {
        params.append('search', search.trim());
      }

      const response = await api.get(`/cati/interviewer/lines?${params}`);
      
      if (response.data.success) {
        const data = response.data.data;
        setLines(data.data || []);
        setLinesPagination({
          current_page: data.current_page,
          last_page: data.last_page,
          per_page: data.per_page,
          total: data.total,
          from: data.from,
          to: data.to
        });
      }
    } catch (error) {
      console.error('Error fetching lines:', error);
    } finally {
      setLoadingLines(false);
    }
  };

  const handleLinesSearch = () => {
    setLinesCurrentPage(1);
    fetchLines(linesSearchTerm);
  };

  const handleExportLines = async () => {
    if (!selectedSurvey) return;
    
    try {
      setExportingLines(true);
      const params = new URLSearchParams({
        survey_id: selectedSurvey.id
      });
      
      // Apply the same filters as the current view
      if (linesStatusFilter !== 'all') {
        if (linesStatusFilter === 'pending') {
          // En attente: pending lines from active uploads only (not_done)
          params.append('completed', 'false');
          params.append('upload_status', 'not_done');
        } else if (linesStatusFilter === 'deactivated') {
          // Désactivé: pending lines from deactivated uploads (reported)
          params.append('completed', 'false');
          params.append('upload_status', 'reported');
        } else if (linesStatusFilter === 'completed') {
          params.append('completed', 'true');
        } else {
          params.append('result_status', linesStatusFilter);
        }
      }

      // Add search parameter if provided
      if (linesSearchTerm && linesSearchTerm.trim() !== '') {
        params.append('search', linesSearchTerm.trim());
      }

      const response = await api.get(`/cati/interviewer/lines/export?${params}`, {
        responseType: 'blob'
      });
      
      // Create blob and download
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Get filename from Content-Disposition header or use default
      const contentDisposition = response.headers['content-disposition'];
      let filename = `export_lignes_${selectedSurvey?.title || 'survey'}_${new Date().toISOString().split('T')[0]}.xlsx`;
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="(.+)"/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }
      
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting lines:', error);
      alert('Erreur lors de l\'exportation');
    } finally {
      setExportingLines(false);
    }
  };

  const handleSurveyClick = (survey) => {
    navigate(`/cati/interview-uploads/survey/${survey.id}`);
  };


  const handleSelectUpload = (uploadId) => {
    setSelectedUploads(prev => {
      if (prev.includes(uploadId)) {
        return prev.filter(id => id !== uploadId);
      } else {
        return [...prev, uploadId];
      }
    });
  };

  const handleSelectAll = () => {
    if (selectedUploads.length === uploads.length) {
      setSelectedUploads([]);
    } else {
      setSelectedUploads(uploads.map(u => u.id));
    }
  };

  const handleExportSelected = async () => {
    if (selectedUploads.length === 0) return;
    
    try {
      setExporting(true);
      const response = await api.post('/cati/interview-uploads/export-multiple', {
        upload_ids: selectedUploads
      }, {
        responseType: 'blob'
      });
      
      // Create blob and download
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Get filename from Content-Disposition header or use default
      const contentDisposition = response.headers['content-disposition'];
      let filename = `export_${selectedSurvey?.title || 'uploads'}_${new Date().toISOString().split('T')[0]}.xlsx`;
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="(.+)"/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }
      
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      // Clear selection after export
      setSelectedUploads([]);
    } catch (error) {
      console.error('Error exporting:', error);
      alert('Erreur lors de l\'exportation');
    } finally {
      setExporting(false);
    }
  };

  const handleSearchInputChange = (value) => {
    setSearchInputValue(value); // Only update local input, don't trigger API call
  };

  const handleFilesSearch = () => {
    setSearchTerm(searchInputValue); // Set the actual search term which triggers the API call
    setCurrentPage(1);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getFileIcon = (fileName) => {
    if (fileName.match(/\.csv$/i)) {
      return faFileCsv;
    }
    return faFileExcel;
  };

  const getFileIconColor = (fileName) => {
    if (fileName.match(/\.csv$/i)) {
      return 'text-green-600';
    }
    return 'text-blue-600';
  };

  const getResultStatusBadge = (status) => {
    const statusConfig = {
      'ok': { bg: 'bg-green-100', text: 'text-green-800', label: 'OK', icon: faCheckCircle },
      'boite_vocal': { bg: 'bg-blue-100', text: 'text-blue-800', label: 'Boîte Vocale', icon: faVolumeDown },
      'refus': { bg: 'bg-red-100', text: 'text-red-800', label: 'Refus', icon: faTimesCircle },
      'hors_cible': { bg: 'bg-orange-100', text: 'text-orange-800', label: 'Hors Cible', icon: faBan },
      'rendez_vous': { bg: 'bg-purple-100', text: 'text-purple-800', label: 'Rendez-vous', icon: faCalendarCheck },
      'injoignable': { bg: 'bg-gray-100', text: 'text-gray-800', label: 'Injoignable', icon: faPhoneSlash },
      'faux_numero': { bg: 'bg-pink-100', text: 'text-pink-800', label: 'Faux Numéro', icon: faPhoneAlt }
    };
    
    if (!status) {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
          En attente
        </span>
      );
    }
    
    const config = statusConfig[status] || { bg: 'bg-gray-100', text: 'text-gray-800', label: status, icon: faInfoCircle };
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.bg} ${config.text}`}>
        <FontAwesomeIcon icon={config.icon} className="mr-1" />
        {config.label}
      </span>
    );
  };

  const getUploadStatusBadge = (status) => {
    if (!status) {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
          -
        </span>
      );
    }
    
    if (status === 'done') {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
          Terminé
        </span>
      );
    } else if (status === 'reported') {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
          Désactivé
        </span>
      );
    } else {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
          Non terminé
        </span>
      );
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 relative overflow-hidden">
      {/* Dotted Background */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0" style={{
          backgroundImage: 'radial-gradient(circle, #dc2626 1px, transparent 1px)',
          backgroundSize: '20px 20px'
        }}></div>
      </div>

      <div className="relative z-10 p-6">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
              <div>
                <h1 className="text-4xl font-bold text-gray-900 mb-2">AFFECTATIONS DES BASES DE DONNÉES CATI</h1>
                <p className="text-gray-600">
                  {view === 'surveys' 
                    ? 'Sélectionnez une enquête pour voir ses fichiers' 
                    : `Fichiers pour: ${selectedSurvey?.title || ''}`}
                </p>
              </div>
              {(user?.role === 'resp_cati' || user?.role === 'supervisor_cati') && (
                <button
                  onClick={() => navigate('/cati/interview-uploads/create')}
                  className="w-full sm:w-auto flex items-center justify-center px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                >
                  <FontAwesomeIcon icon={faPlus} className="mr-2" />
                  Nouveau Téléchargement
                </button>
              )}
            </div>

            {view === 'uploads' && (
              <>
                {/* Statistics Section */}
                {loadingStats ? (
                  <div className="mb-6 text-center py-8">
                    <FontAwesomeIcon icon={faSpinner} className="text-2xl text-red-600 animate-spin mb-2" />
                    <p className="text-gray-600">Chargement des statistiques...</p>
                  </div>
                ) : surveyStats && (
                  <>
                    {/* Stats Cards */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
                      <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-6" style={{ minHeight: '140px' }}>
                        <div className="flex items-center justify-between h-full">
                          <div>
                            <p className="text-sm text-gray-600 mb-1">Total Lignes</p>
                            <p className="text-3xl font-bold text-gray-900">{surveyStats.total}</p>
                          </div>
                          <div className="bg-blue-100 p-3 rounded-lg">
                            <FontAwesomeIcon icon={faFileAlt} className="text-2xl text-blue-600" />
                          </div>
                        </div>
                      </div>

                      <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-6" style={{ minHeight: '140px' }}>
                        <div className="flex items-center justify-between h-full">
                          <div>
                            <p className="text-sm text-gray-600 mb-1">Complétées</p>
                            <p className="text-3xl font-bold text-green-600">{surveyStats.completed}</p>
                          </div>
                          <div className="bg-green-100 p-3 rounded-lg">
                            <FontAwesomeIcon icon={faCheckCircle} className="text-2xl text-green-600" />
                          </div>
                        </div>
                      </div>

                      <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-6" style={{ minHeight: '140px' }}>
                        <div className="flex items-center justify-between h-full">
                          <div>
                            <p className="text-sm text-gray-600 mb-1">En attente</p>
                            <p className="text-3xl font-bold text-yellow-600">{surveyStats.pending}</p>
                          </div>
                          <div className="bg-yellow-100 p-3 rounded-lg">
                            <FontAwesomeIcon icon={faClock} className="text-2xl text-yellow-600" />
                          </div>
                        </div>
                      </div>

                      <div 
                        className="relative bg-white rounded-xl shadow-lg border border-gray-100 cursor-pointer group hover:shadow-xl transition-shadow duration-300"
                        style={{ perspective: '1000px', minHeight: '140px' }}
                        onClick={() => setIsCardFlipped(!isCardFlipped)}
                      >
                        {/* Flipped corner indicator */}
                        <div className="absolute top-0 right-0 w-0 h-0 border-l-[30px] border-l-transparent border-t-[30px] border-t-gray-300 rounded-tr-xl z-10 group-hover:border-t-green-400 transition-colors duration-300">
                          <div className="absolute -top-[30px] right-0 w-0 h-0 border-l-[25px] border-l-transparent border-t-[25px] border-t-white"></div>
                        </div>
                        
                        <div 
                          className="relative w-full h-full transition-transform duration-500"
                          style={{ 
                            transformStyle: 'preserve-3d',
                            transform: isCardFlipped ? 'rotateY(180deg)' : 'rotateY(0deg)',
                            minHeight: '140px'
                          }}
                        >
                          {/* Front side */}
                          <div 
                            className="absolute inset-0 p-6 rounded-xl"
                            style={{ backfaceVisibility: 'hidden', WebkitBackfaceVisibility: 'hidden' }}
                          >
                            <div className="flex items-center justify-between h-full">
                              <div>
                                <p className="text-sm text-gray-600 mb-1">Réussies (OK)</p>
                                <p className="text-3xl font-bold text-green-600">{surveyStats.by_status.ok}</p>
                              </div>
                              <div className="bg-green-100 p-3 rounded-lg">
                                <FontAwesomeIcon icon={faCheckCircle} className="text-2xl text-green-600" />
                              </div>
                            </div>
                          </div>
                          
                          {/* Back side */}
                          <div 
                            className="absolute inset-0 p-6 rounded-xl"
                            style={{ 
                              backfaceVisibility: 'hidden',
                              WebkitBackfaceVisibility: 'hidden',
                              transform: 'rotateY(180deg)'
                            }}
                          >
                            <div className="flex items-center justify-between h-full">
                              <div>
                                <p className="text-sm text-gray-600 mb-1">Réussies LimeSurvey</p>
                                <p className="text-3xl font-bold" style={{ color: '#059669' }}>
                                  {surveyStats.limesurvey_completed ?? 0}
                                </p>
                              </div>
                              <div className="p-3 rounded-lg" style={{ backgroundColor: '#d1fae5' }}>
                                <FontAwesomeIcon icon={faFileAlt} className="text-2xl" style={{ color: '#059669' }} />
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Status Breakdown */}
                    <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-6 mb-6">
                      <h2 className="text-xl font-bold text-gray-900 mb-4">Répartition par Statut</h2>
                      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
                        {Object.entries(surveyStats.by_status).map(([status, count]) => {
                          if (status === 'null') return null;
                          const statusLabels = {
                            'ok': 'OK',
                            'boite_vocal': 'Boîte Vocale',
                            'refus': 'Refus',
                            'hors_cible': 'Hors Cible',
                            'rendez_vous': 'Rendez-vous',
                            'injoignable': 'Injoignable',
                            'faux_numero': 'Faux Numéro'
                          };
                          return (
                            <div key={status} className="text-center p-4 bg-gray-50 rounded-lg">
                              <p className="text-2xl font-bold text-gray-900">{count}</p>
                              <p className="text-xs text-gray-600 mt-1">{statusLabels[status] || status}</p>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </>
                )}

                {/* Files Section - Collapsible */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                  <button
                    onClick={() => setShowFilesSection(!showFilesSection)}
                    className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-gray-50 transition-colors duration-200"
                  >
                    <h3 className="text-lg font-semibold text-gray-900">
                      Rechercher et Gérer les Fichiers
                    </h3>
                    <FontAwesomeIcon 
                      icon={showFilesSection ? faChevronUp : faChevronDown} 
                      className="text-gray-500 transition-transform duration-200"
                    />
                  </button>
                  
                  {showFilesSection && (
                    <div className="px-6 pb-6 border-t border-gray-100">
                      {/* Search Bar */}
                      <div className="flex items-center gap-4 mb-4 pt-4">
                        <div className="relative flex-1">
                          <FontAwesomeIcon icon={faSearch} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                          <input
                            type="text"
                            placeholder="Rechercher par nom de fichier ou enquêteur..."
                            value={searchInputValue}
                            onChange={(e) => handleSearchInputChange(e.target.value)}
                            onKeyPress={(e) => {
                              if (e.key === 'Enter') {
                                handleFilesSearch();
                              }
                            }}
                            className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                          />
                        </div>
                        <button
                          onClick={handleFilesSearch}
                          disabled={loading}
                          className="px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                        >
                          {loading ? (
                            <FontAwesomeIcon icon={faSpinner} className="animate-spin" />
                          ) : (
                            <>
                              <FontAwesomeIcon icon={faSearch} className="mr-2" />
                              Rechercher
                            </>
                          )}
                        </button>
                        {selectedUploads.length > 0 && (
                          <button
                            onClick={handleExportSelected}
                            disabled={exporting}
                            className="flex items-center px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                          >
                            {exporting ? (
                              <>
                                <FontAwesomeIcon icon={faSpinner} className="mr-2 animate-spin" />
                                Export en cours...
                              </>
                            ) : (
                              <>
                                <FontAwesomeIcon icon={faDownload} className="mr-2" />
                                Exporter ({selectedUploads.length})
                              </>
                            )}
                          </button>
                        )}
                      </div>

                      {/* Uploads Table */}
                      {loading ? (
                        <div className="p-12 text-center">
                          <FontAwesomeIcon icon={faSpinner} className="text-4xl text-red-600 animate-spin mb-4" />
                          <p className="text-gray-600">Chargement des fichiers...</p>
                        </div>
                      ) : uploads.length === 0 ? (
                        <div className="p-12 text-center">
                          <FontAwesomeIcon icon={faFileAlt} className="text-6xl text-gray-300 mb-4" />
                          <p className="text-gray-600 text-lg mb-2">Aucun fichier trouvé</p>
                          <p className="text-gray-500">Commencez par télécharger un fichier d'interviews</p>
                        </div>
                      ) : (
                        <>
                          <div className="overflow-x-auto">
                            <table className="w-full">
                              <thead className="bg-gray-50 border-b border-gray-200">
                                <tr>
                                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-12">
                                    <input
                                      type="checkbox"
                                      checked={uploads.length > 0 && selectedUploads.length === uploads.length}
                                      onChange={handleSelectAll}
                                      className="rounded border-gray-300 text-red-600 focus:ring-red-500"
                                    />
                                  </th>
                                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Fichier
                                  </th>
                                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Enquête
                                  </th>
                                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Statut
                                  </th>
                                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Téléchargé le
                                  </th>
                                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Téléchargé par
                                  </th>
                                </tr>
                              </thead>
                              <tbody className="bg-white divide-y divide-gray-200">
                                {uploads.map((upload) => (
                                  <tr 
                                    key={upload.id} 
                                    onClick={() => navigate(`/cati/interview-uploads/${upload.id}`)}
                                    className="hover:bg-gray-50 cursor-pointer transition-colors"
                                  >
                                    <td className="px-6 py-4 whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                                      <input
                                        type="checkbox"
                                        checked={selectedUploads.includes(upload.id)}
                                        onChange={() => handleSelectUpload(upload.id)}
                                        className="rounded border-gray-300 text-red-600 focus:ring-red-500"
                                      />
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                      <div className="flex items-center">
                                        <FontAwesomeIcon 
                                          icon={getFileIcon(upload.file_name)} 
                                          className={`${getFileIconColor(upload.file_name)} mr-2`} 
                                        />
                                        <span className="text-sm font-medium text-gray-900">
                                          {upload.file_name}
                                        </span>
                                      </div>
                                      {upload.data && Array.isArray(upload.data) && (
                                        <span className="text-xs text-gray-500">
                                          {upload.data.length} {upload.data.length === 1 ? 'ligne' : 'lignes'}
                                        </span>
                                      )}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                      <div className="text-sm text-gray-900">
                                        {upload.survey?.title || '-'}
                                      </div>
                                      {upload.survey?.limesurvey_id && (
                                        <div className="text-xs text-gray-500">
                                          ID: {upload.survey.limesurvey_id}
                                        </div>
                                      )}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                        upload.status === 'done' 
                                          ? 'bg-green-100 text-green-800' 
                                          : upload.status === 'reported' 
                                          ? 'bg-red-100 text-red-800' 
                                          : 'bg-yellow-100 text-yellow-800'
                                      }`}>
                                        {upload.status === 'done' 
                                          ? 'Terminé' 
                                          : upload.status === 'reported' 
                                          ? 'Désactivé' 
                                          : 'Non terminé'}
                                      </span>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                      <div className="flex items-center text-sm text-gray-500">
                                        <FontAwesomeIcon icon={faCalendarAlt} className="mr-2" />
                                        {formatDate(upload.created_at)}
                                      </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                      <span className="text-sm text-gray-900">
                                        {upload.uploaded_by?.name || '-'}
                                      </span>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>

                          {/* Pagination */}
                          {pagination.last_page > 1 && (
                            <div className="bg-gray-50 px-6 py-4 border-t border-gray-200 mt-4">
                              <div className="flex items-center justify-between">
                                <div className="text-sm text-gray-700">
                                  Affichage de <span className="font-medium">{pagination.from}</span> à{' '}
                                  <span className="font-medium">{pagination.to}</span> sur{' '}
                                  <span className="font-medium">{pagination.total}</span> résultats
                                </div>
                                <div className="flex gap-2">
                                  <button
                                    onClick={() => handlePageChange(pagination.current_page - 1)}
                                    disabled={pagination.current_page === 1}
                                    className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                  >
                                    Précédent
                                  </button>
                                  <span className="px-4 py-2 text-sm text-gray-700">
                                    Page {pagination.current_page} sur {pagination.last_page}
                                  </span>
                                  <button
                                    onClick={() => handlePageChange(pagination.current_page + 1)}
                                    disabled={pagination.current_page === pagination.last_page}
                                    className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                  >
                                    Suivant
                                  </button>
                                </div>
                              </div>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  )}
                </div>

                {/* Lines Section */}
                {view === 'uploads' && (
                  <div ref={linesSectionRef} className="bg-white rounded-xl shadow-lg border border-gray-100 p-6 mt-6">
                    <div className="flex items-center justify-between mb-4">
                      <h2 className="text-xl font-bold text-gray-900">Lignes des questionnaires</h2>
                      <div className="flex items-center space-x-2">
                        <div className="relative flex items-center">
                          <FontAwesomeIcon icon={faSearch} className="absolute left-3 text-gray-400 z-10" />
                          <input
                            type="text"
                            placeholder="Rechercher..."
                            value={linesSearchTerm}
                            onChange={(e) => setLinesSearchTerm(e.target.value)}
                            onKeyPress={(e) => {
                              if (e.key === 'Enter') {
                                handleLinesSearch();
                              }
                            }}
                            className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-red-500 focus:border-red-500 w-64 h-10"
                          />
                          <button
                            onClick={handleLinesSearch}
                            disabled={loadingLines}
                            className="ml-2 px-4 h-10 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                          >
                            {loadingLines ? (
                              <FontAwesomeIcon icon={faSpinner} className="animate-spin" />
                            ) : (
                              <FontAwesomeIcon icon={faSearch} />
                            )}
                          </button>
                        </div>
                        <select
                          value={linesStatusFilter}
                          onChange={(e) => {
                            setLinesStatusFilter(e.target.value);
                            setLinesCurrentPage(1);
                            // Keep search term when changing status filter
                          }}
                          className="px-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-red-500 focus:border-red-500 h-10"
                        >
                          <option value="all">Tous les statuts</option>
                          <option value="pending">En attente</option>
                          <option value="completed">Complétées</option>
                          <option value="deactivated">Désactivé</option>
                          <option value="ok">OK</option>
                          <option value="boite_vocal">Boîte Vocale</option>
                          <option value="refus">Refus</option>
                          <option value="hors_cible">Hors Cible</option>
                          <option value="rendez_vous">Rendez-vous</option>
                          <option value="injoignable">Injoignable</option>
                          <option value="faux_numero">Faux Numéro</option>
                        </select>
                        <button
                          onClick={handleExportLines}
                          disabled={exportingLines}
                          className="flex items-center px-4 h-10 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {exportingLines ? (
                            <>
                              <FontAwesomeIcon icon={faSpinner} className="animate-spin mr-2" />
                              Export...
                            </>
                          ) : (
                            <>
                              <FontAwesomeIcon icon={faDownload} className="mr-2" />
                              Exporter
                            </>
                          )}
                        </button>
                      </div>
                    </div>

                    {loadingLines ? (
                      <div className="text-center py-12">
                        <FontAwesomeIcon icon={faSpinner} className="text-4xl text-red-600 animate-spin mb-4" />
                        <p className="text-gray-600">Chargement des lignes...</p>
                      </div>
                    ) : lines.length === 0 ? (
                      <div className="text-center py-12">
                        <FontAwesomeIcon icon={faFileAlt} className="text-6xl text-gray-300 mb-4" />
                        <p className="text-gray-600">Aucune ligne trouvée</p>
                      </div>
                    ) : (
                      <>
                        {(() => {
                          const hasCodeAgence = lines.some(line => line.code_agence);
                          return (
                            <div className="overflow-x-auto">
                              <table className="w-full">
                                <thead className="bg-gray-50 border-b border-gray-200">
                                  <tr>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                      Enquêteur
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                      Fichier
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                      Statut
                                    </th>
                                    {hasCodeAgence && (
                                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                        Code Agence
                                      </th>
                                    )}
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                      Num_tel
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                      Complété le
                                    </th>
                                  </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                  {lines.map((line) => (
                                    <tr 
                                      key={line.id} 
                                      onClick={() => navigate(`/cati/interview-uploads/${line.upload_id}/lines/${line.id}`)}
                                      className="hover:bg-gray-50 cursor-pointer transition-colors"
                                    >
                                      <td className="px-4 py-3 whitespace-nowrap">
                                        <div className="flex items-center">
                                          <FontAwesomeIcon icon={faUser} className="text-gray-400 mr-2" />
                                          <div>
                                            <div className="text-sm font-medium text-gray-900">
                                              {line.interviewer?.name || '-'}
                                            </div>
                                            {line.interviewer?.email && (
                                              <div className="text-xs text-gray-500">
                                                {line.interviewer.email}
                                              </div>
                                            )}
                                          </div>
                                        </div>
                                      </td>
                                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                                        {line.upload?.file_name || '-'}
                                      </td>
                                      <td className="px-4 py-3 whitespace-nowrap">
                                        {getResultStatusBadge(line.result_status)}
                                      </td>
                                      {hasCodeAgence && (
                                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                                          {line.code_agence || '-'}
                                        </td>
                                      )}
                                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                                        {line.line_data?.Num_tel || line.line_data?.num_tel || line.line_data?.['Num_tel'] || '-'}
                                      </td>
                                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                                        {formatDate(line.completed_at)}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          );
                        })()}

                        {/* Pagination */}
                        {linesPagination.last_page > 1 && (
                          <div className="mt-4 flex items-center justify-between border-t border-gray-200 pt-4">
                            <div className="text-sm text-gray-700">
                              Affichage de <span className="font-medium">{linesPagination.from}</span> à{' '}
                              <span className="font-medium">{linesPagination.to}</span> sur{' '}
                              <span className="font-medium">{linesPagination.total}</span> résultats
                            </div>
                            <div className="flex gap-2">
                              <button
                                onClick={() => {
                                  setLinesCurrentPage(linesCurrentPage - 1);
                                  // Scroll to top of lines section after state update
                                  setTimeout(() => {
                                    if (linesSectionRef.current) {
                                      linesSectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
                                    }
                                  }, 100);
                                }}
                                disabled={linesCurrentPage === 1}
                                className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                Précédent
                              </button>
                              <span className="px-4 py-2 text-sm text-gray-700">
                                Page {linesPagination.current_page} sur {linesPagination.last_page}
                              </span>
                              <button
                                onClick={() => {
                                  setLinesCurrentPage(linesCurrentPage + 1);
                                  // Scroll to top of lines section after state update
                                  setTimeout(() => {
                                    if (linesSectionRef.current) {
                                      linesSectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
                                    }
                                  }, 100);
                                }}
                                disabled={linesCurrentPage === linesPagination.last_page}
                                className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                Suivant
                              </button>
                            </div>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                )}
              </>
            )}
          </div>

          {/* Surveys View */}
          {view === 'surveys' && (
            <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
              {/* Search and Filter Section */}
              <div className="p-6 border-b border-gray-200">
                <div className="flex flex-col md:flex-row gap-4">
                  {/* Search Input */}
                  <div className="relative flex-1">
                    <FontAwesomeIcon icon={faSearch} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Rechercher par enquête, enquêteur, téléphone, agence, fichier..."
                      value={surveySearchTerm}
                      onChange={(e) => setSurveySearchTerm(e.target.value)}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          if (surveySearchTerm || surveyFilter !== 'all') {
                            performGlobalSearch(1);
                          } else {
                            setIsSearchMode(false);
                            fetchSurveys();
                          }
                        }
                      }}
                      className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                    />
                  </div>
                  {/* Filter Dropdown */}
                  <select
                    value={surveyFilter}
                    onChange={(e) => {
                      const newFilter = e.target.value;
                      setSurveyFilter(newFilter);
                      // Automatically trigger search when filter changes
                      if (newFilter !== 'all' || surveySearchTerm) {
                        performGlobalSearch(1, newFilter);
                      } else {
                        setIsSearchMode(false);
                        fetchSurveys();
                      }
                    }}
                    className="px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 bg-white"
                  >
                    <option value="all">Toutes les enquêtes</option>
                    <option value="pending">En attente</option>
                    <option value="completed">Complétées</option>
                    <option value="deactivated">Désactivé</option>
                  </select>
                  {/* Search Button */}
                  <button
                    onClick={() => {
                      if (surveySearchTerm || surveyFilter !== 'all') {
                        performGlobalSearch(1);
                      } else {
                        setIsSearchMode(false);
                        fetchSurveys();
                      }
                    }}
                    disabled={loadingSurveys}
                    className="px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                  >
                    {loadingSurveys ? (
                      <FontAwesomeIcon icon={faSpinner} className="animate-spin" />
                    ) : (
                      <FontAwesomeIcon icon={faSearch} className="mr-2" />
                    )}
                    Rechercher
                  </button>
                </div>
                {/* Results count */}
                {!loadingSurveys && isSearchMode && searchResults.length > 0 && (
                  <p className="text-sm text-gray-600 mt-3">
                    {searchResultsPagination.total} {searchResultsPagination.total === 1 ? 'ligne trouvée' : 'lignes trouvées'}
                  </p>
                )}
                {!loadingSurveys && !isSearchMode && surveys.length > 0 && (
                  <p className="text-sm text-gray-600 mt-3">
                    {surveys.length} {surveys.length === 1 ? 'enquête trouvée' : 'enquêtes trouvées'}
                  </p>
                )}
              </div>

              {/* Search Results - Lines Table */}
              {isSearchMode ? (
                loadingSurveys ? (
                  <div className="p-12 text-center">
                    <FontAwesomeIcon icon={faSpinner} className="text-4xl text-red-600 animate-spin mb-4" />
                    <p className="text-gray-600">Recherche en cours...</p>
                  </div>
                ) : searchResults.length === 0 ? (
                  <div className="p-12 text-center">
                    <FontAwesomeIcon icon={faSearch} className="text-6xl text-gray-300 mb-4" />
                    <p className="text-gray-600 text-lg mb-2">Aucune ligne ne correspond à votre recherche</p>
                    <p className="text-gray-500">Essayez de modifier vos critères de recherche</p>
                  </div>
                ) : (
                  <>
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead className="bg-gray-50 border-b border-gray-200">
                          <tr>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                              Enquête
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                              Fichier
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                              Enquêteur
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                              Statut Ligne
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                              Statut Upload
                            </th>
                            {searchResults.some(line => line.code_agence) && (
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                Code Agence
                              </th>
                            )}
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                              Num_tel
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                              Complété le
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {searchResults.map((line) => (
                            <tr 
                              key={line.id} 
                              onClick={() => navigate(`/cati/interview-uploads/${line.upload_id}/lines/${line.id}`)}
                              className="hover:bg-gray-50 cursor-pointer transition-colors"
                            >
                              <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                                {line.upload?.survey?.title || '-'}
                                {line.upload?.survey?.limesurvey_id && (
                                  <div className="text-xs text-gray-500">ID: {line.upload.survey.limesurvey_id}</div>
                                )}
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                                {line.upload?.file_name || '-'}
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap">
                                <div className="flex items-center">
                                  <FontAwesomeIcon icon={faUser} className="text-gray-400 mr-2" />
                                  <div>
                                    <div className="text-sm font-medium text-gray-900">
                                      {line.interviewer?.name || '-'}
                                    </div>
                                    {line.interviewer?.email && (
                                      <div className="text-xs text-gray-500">
                                        {line.interviewer.email}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap">
                                {getResultStatusBadge(line.result_status)}
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap">
                                {getUploadStatusBadge(line.upload?.status)}
                              </td>
                              {searchResults.some(l => l.code_agence) && (
                                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                                  {line.code_agence || '-'}
                                </td>
                              )}
                              <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                                {line.line_data?.Num_tel || line.line_data?.num_tel || line.line_data?.['Num_tel'] || '-'}
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                                {formatDate(line.completed_at)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    {/* Pagination */}
                    {searchResultsPagination.last_page > 1 && (
                      <div className="mt-4 flex items-center justify-between border-t border-gray-200 pt-4 px-6 pb-6">
                        <div className="text-sm text-gray-700">
                          Affichage de <span className="font-medium">{searchResultsPagination.from}</span> à{' '}
                          <span className="font-medium">{searchResultsPagination.to}</span> sur{' '}
                          <span className="font-medium">{searchResultsPagination.total}</span> résultats
                        </div>
                        <div className="flex gap-2">
                          <button
                            onClick={() => performGlobalSearch(searchResultsPage - 1)}
                            disabled={searchResultsPage === 1}
                            className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            Précédent
                          </button>
                          <span className="px-4 py-2 text-sm text-gray-700">
                            Page {searchResultsPagination.current_page} sur {searchResultsPagination.last_page}
                          </span>
                          <button
                            onClick={() => performGlobalSearch(searchResultsPage + 1)}
                            disabled={searchResultsPage === searchResultsPagination.last_page}
                            className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            Suivant
                          </button>
                        </div>
                      </div>
                    )}
                  </>
                )
              ) : (
                /* Surveys View - Default */
                loadingSurveys ? (
                  <div className="p-12 text-center">
                    <FontAwesomeIcon icon={faSpinner} className="text-4xl text-red-600 animate-spin mb-4" />
                    <p className="text-gray-600">Chargement des enquêtes...</p>
                  </div>
                ) : surveys.length === 0 ? (
                  <div className="p-12 text-center">
                    <FontAwesomeIcon icon={faClipboardList} className="text-6xl text-gray-300 mb-4" />
                    <p className="text-gray-600 text-lg mb-2">Aucune enquête trouvée</p>
                    <p className="text-gray-500">Commencez par télécharger un fichier d'interviews</p>
                  </div>
                ) : (() => {
                  const surveysLastPage = Math.ceil(surveys.length / SURVEYS_PER_PAGE);
                  const surveysFrom = (surveysPage - 1) * SURVEYS_PER_PAGE;
                  const pagedSurveys = surveys.slice(surveysFrom, surveysFrom + SURVEYS_PER_PAGE);
                  const handleSurveysPageChange = (page) => {
                    setSurveysPage(page);
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  };
                  return (
                  <>
                  <div className="p-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {pagedSurveys.map((survey) => (
                        <button
                          key={survey.id}
                          onClick={() => handleSurveyClick(survey)}
                          className="bg-white border-2 border-gray-200 rounded-xl p-6 hover:border-red-500 hover:shadow-lg transition-all text-left group"
                        >
                          <div className="flex items-center justify-between mb-4">
                            <div className="bg-red-100 p-3 rounded-lg">
                              <FontAwesomeIcon icon={faClipboardList} className="text-2xl text-red-600" />
                            </div>
                            <FontAwesomeIcon 
                              icon={faChevronRight} 
                              className="text-gray-400 group-hover:text-red-600 transition-colors" 
                            />
                          </div>
                          <h3 className="text-lg font-bold text-gray-900 mb-2">{survey.title}</h3>
                          {survey.limesurvey_id && (
                            <p className="text-sm text-gray-500 mb-3">ID: {survey.limesurvey_id}</p>
                          )}
                          <div className="flex items-center text-sm text-gray-600">
                            <FontAwesomeIcon icon={faFileAlt} className="mr-2" />
                            <span className="font-medium">{survey.upload_count} {survey.upload_count === 1 ? 'fichier' : 'fichiers'}</span>
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Pagination */}
                  {surveysLastPage > 1 && (
                    <div className="bg-white px-4 sm:px-6 py-4 border-t border-gray-200">
                      <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                        {/* Mobile: Show page info */}
                        <div className="sm:hidden text-sm text-gray-700 text-center w-full">
                          Page {surveysPage} sur {surveysLastPage}
                        </div>

                        {/* Desktop: Show full info */}
                        <div className="hidden sm:block text-sm text-gray-700">
                          <span>
                            Affichage de <span className="font-medium">{surveysFrom + 1}</span> à{' '}
                            <span className="font-medium">{surveysFrom + pagedSurveys.length}</span> sur{' '}
                            <span className="font-medium">{surveys.length}</span> résultats
                          </span>
                        </div>

                        <div className="flex items-center space-x-2 w-full sm:w-auto justify-center sm:justify-end">
                          <button
                            onClick={() => handleSurveysPageChange(surveysPage - 1)}
                            disabled={surveysPage === 1}
                            className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                          >
                            <FontAwesomeIcon icon={faChevronLeft} className="w-4 h-4" />
                          </button>

                          <div className="flex space-x-1">
                            {Array.from({ length: Math.min(5, surveysLastPage) }, (_, i) => {
                              let pageNum;
                              if (surveysLastPage <= 5) {
                                pageNum = i + 1;
                              } else if (surveysPage <= 3) {
                                pageNum = i + 1;
                              } else if (surveysPage >= surveysLastPage - 2) {
                                pageNum = surveysLastPage - 4 + i;
                              } else {
                                pageNum = surveysPage - 2 + i;
                              }

                              return (
                                <button
                                  key={pageNum}
                                  onClick={() => handleSurveysPageChange(pageNum)}
                                  className={`px-3 py-2 text-sm font-medium border rounded-lg transition-colors ${
                                    surveysPage === pageNum
                                      ? 'bg-red-600 text-white border-red-600'
                                      : 'text-gray-700 bg-white border-gray-300 hover:bg-gray-50'
                                  }`}
                                >
                                  {pageNum}
                                </button>
                              );
                            })}
                          </div>

                          <button
                            onClick={() => handleSurveysPageChange(surveysPage + 1)}
                            disabled={surveysPage === surveysLastPage}
                            className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                          >
                            <FontAwesomeIcon icon={faChevronRight} className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                  </>
                  );
                })()
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default InterviewUploads;

